// Jobs feature constants.

import { JOB_TYPES } from "../../constants";
import type { AfeClass } from "./types";

/** The canonical job-type options. SINGLE SOURCE is JOB_TYPES in src/constants (PO-ratified) — this
 *  re-exports it; do NOT re-author labels here (that drifts the picklist, the exact failure CLAUDE.md
 *  Data Model Integrity warns against). Each value maps to a timeline paradigm via
 *  timelinePatternForJobType(). */
export const JOB_TYPE_OPTIONS = JOB_TYPES;

/** Default job type for a new job (the most common operation). */
export const DEFAULT_JOB_TYPE = "construction";

/** Friendly label for a job-type value (from the canonical JOB_TYPES; raw value if unknown). */
export function jobTypeLabel(value: string | undefined): string {
  if (!value) return "—";
  return JOB_TYPES.find((o) => o.value === value)?.label ?? value;
}

/** AFE type — an original authorization or a supplemental (overrun / scope addition) to one. */
export const AFE_TYPE_OPTIONS = [
  { value: "original", label: "Original" },
  { value: "supplemental", label: "Supplemental" },
] as const;

export function afeTypeLabel(value: string | undefined): string {
  if (!value) return "—";
  return AFE_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** How an AFE identifies itself in UI (PO 2026-08-24). The engineer's `name` is the primary handle
 *  and is always present (migrateAfe guarantees it); the accounting-issued `afeNumber` is absent
 *  until submission/approval. Callers that need BOTH render them separately — this is the one-line
 *  form for lists, chips, and headers.
 *
 *  Deliberately does NOT invent a number when one is absent: a draft has no issued number, and
 *  showing a fabricated one is the failure this split exists to prevent. */
export function afeLabel(afe: {
  name?: string | undefined;
  afeNumber?: string | undefined;
}): string {
  const name = afe.name?.trim();
  const number = afe.afeNumber?.trim();
  if (name && number) return `${name} · ${number}`;
  return name || number || "—";
}

/** Which AFE class a job of a given type most naturally funds (PO 2026-07-27 — AFEs are per-job,
 *  any type). Drives the default `afeClass` when seeding an AFE for a job + which sibling class a
 *  clone resolves against. Keys cover the canonical JOB_TYPES values plus the buildWell/legacy
 *  aliases ('drilling', 'abandonment'). A jobType with no mapping → undefined (the user picks the
 *  class explicitly). `facilities` is intentionally absent — it is the pad AFE, not a job type. */
export const JOB_TYPE_TO_AFE_CLASS: Record<string, AfeClass> = {
  construction: "primary_drilling",
  drilling: "primary_drilling", // buildWell / WellView-corpus alias
  stimulation: "primary_completion",
  treatment: "workover", // chemical treatment / flow assurance = an intervention
  workover: "workover",
  abandon: "abandonment",
  abandonment: "abandonment", // alias
};

/** The AFE class a job of `jobType` funds, or undefined if the type has no natural class. */
export function afeClassForJobType(
  jobType: string | undefined,
): AfeClass | undefined {
  if (!jobType) return undefined;
  return JOB_TYPE_TO_AFE_CLASS[jobType];
}

/** Human-facing labels for the five AFE classes (§7 PO 2026-07-24). */
export const AFE_CLASS_LABELS: Record<AfeClass, string> = {
  primary_drilling: "Primary Drilling (D&C)",
  primary_completion: "Primary Completion",
  workover: "Workover / Intervention",
  abandonment: "Plugging & Abandonment",
  facilities: "Facilities / Pad",
};

/** Friendly label for an AfeClass (raw value if somehow out of set). */
export function afeClassLabel(afeClass: AfeClass | undefined): string {
  if (!afeClass) return "—";
  return AFE_CLASS_LABELS[afeClass] ?? afeClass;
}

// ── AFE line-item picklists (AFE S4 Builder) ────────────────────────────────
// The canonical tokens are the prebake DC_BREAKDOWN + PAD_LINES phases in
// `apps/compute-server/scripts/prebake/afe.py` (the FE mirror is `afeTemplates/seeds.ts`). These are
// DISPLAY-ONLY label maps + Select options — Charter Abs-2: no math, they only classify a line.

/** AFE line activity phases — well D&C (MOB…CONTINGENCY) + pad/facilities (CIVIL/FACILITY/PERMIT). */
export const ACTIVITY_PHASE_OPTIONS = [
  { value: "MOB", label: "Mobilization" },
  { value: "DRILL", label: "Drilling" },
  { value: "CASING", label: "Casing" },
  { value: "EVAL", label: "Evaluation" },
  { value: "COMPLETION", label: "Completion" },
  { value: "SUPPORT", label: "Support" },
  { value: "CONTINGENCY", label: "Contingency" },
  { value: "CIVIL", label: "Civil / Location" },
  { value: "FACILITY", label: "Facilities" },
  { value: "PERMIT", label: "Permitting" },
] as const;

/** Friendly label for an activity phase (raw value if off-list). */
export function activityPhaseLabel(value: string | undefined): string {
  if (!value) return "—";
  return ACTIVITY_PHASE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** IDC (intangible, expensed) vs tangible (capitalized) — the tangible/intangible subtotal driver. */
export const COST_CLASS_OPTIONS = [
  { value: "INTANGIBLE", label: "Intangible" },
  { value: "TANGIBLE", label: "Tangible" },
] as const;

/** Friendly label for a cost class (raw value if off-list). */
export function costClassLabel(value: string | undefined): string {
  if (!value) return "—";
  return COST_CLASS_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** Dry-hole vs completion — the per-line override; the DEFAULT is Python's method-aware map (S2). */
export const DC_PHASE_OPTIONS = [
  { value: "dry_hole", label: "Dry Hole" },
  { value: "completion", label: "Completion" },
] as const;

/** Per-line pricing mode (§7 decision 4 — HYBRID): a LUMP amount OR quantity × unit-rate. */
export const LINE_PRICING_MODES = [
  { value: "lump", label: "Lump" },
  { value: "unit", label: "Unit" },
] as const;
export type LinePricingMode = (typeof LINE_PRICING_MODES)[number]["value"];

/** Cross-well pad-cost allocation basis (§3.3 / reopened §7.7 — the Viewer selector). `footage`
 *  needs a per-well TD (deferred v1 source); the engine 400s if chosen without it (Abs-4). */
export const ALLOCATION_BASIS_OPTIONS = [
  { value: "equal", label: "Equal per well" },
  { value: "footage", label: "By footage (needs TD)" },
  { value: "dc_weight", label: "By D&C weight" },
] as const;

/** Friendly label for an allocation basis (raw value if off-list). */
export function allocationBasisLabel(value: string | undefined): string {
  if (!value) return "—";
  return (
    ALLOCATION_BASIS_OPTIONS.find((o) => o.value === value)?.label ?? value
  );
}

/** Completion method — drives the method-aware dry-hole/completion default in the Python engine (S2). */
export const COMPLETION_METHOD_OPTIONS = [
  { value: "plug_and_perf", label: "Plug & Perf (cased)" },
  { value: "slotted_liner", label: "Slotted Liner" },
  { value: "gravel_pack", label: "Gravel Pack" },
  { value: "openhole", label: "Open Hole" },
  { value: "cased_hole_other", label: "Cased Hole (other)" },
  {
    value: "coaxial_exchanger",
    label: "Coaxial exchanger (closed loop)",
  },
] as const;

/** Operations-lifecycle status for a job (free-text axis; these are the standard values a new job
 *  moves through — DEFAULT_PAD_JOB_STATUS = "Planned"). */
export const JOB_STATUS_OPTIONS = [
  { value: "Planned", label: "Planned" },
  { value: "Active", label: "Active" },
  { value: "Complete", label: "Complete" },
  { value: "Suspended", label: "Suspended" },
  { value: "Cancelled", label: "Cancelled" },
] as const;

/** Keep an existing out-of-list value selectable — a prebaked non-standard type/status (e.g. the
 *  WellView corpus's lowercase "drilling"/"completed") must display truthfully and round-trip, not
 *  silently snap to the first option (which would corrupt the real value on save). Shared by the job
 *  identity band's Type/Status selects (was inlined in the deleted JobEditModal). */
export function withCurrent(
  options: readonly { value: string; label: string }[],
  current: string | undefined,
): { value: string; label: string }[] {
  const base = options.map((o) => ({ value: o.value, label: o.label }));
  if (current && !base.some((o) => o.value === current)) {
    return [{ value: current, label: current }, ...base];
  }
  return base;
}
